'use client'

import Image from 'next/image'

import { Link } from '@/components/common/Link/Link'
import DynamicIcon from '@/components/primitives/DynamicIcon/DynamicIcon'
import { getImageUrl } from '@/lib/utils'

import type { QuickLinkType } from '@/types/contentful/quickLinkType'

// This is for the 'divider' elements between the links
const quickLinkItemClassName =
  "after:pointer-events-none after:hidden last:lg:after:hidden lg:relative lg:after:absolute lg:after:right-0 lg:after:top-1/2 lg:after:z-10 lg:after:block lg:after:h-12 lg:after:w-px lg:after:-translate-y-1/2 lg:after:bg-gradient-to-b lg:after:from-white lg:after:via-grey-200 lg:after:to-white lg:after:content-['']"

export const QuickLinks = ({ links }: QuickLinkType) => {
  if (!links?.length) return null

  const lastItemSpansFullWidth = links.length % 2 === 1

  return (
    <nav data-name="quick-links" aria-label="Quick links" className="size-full border-b border-grey-200 bg-white shadow-[0_0_16px_0_rgba(0,0,0,0.06)] lg:h-[82px]">
      <ul className="m-0 grid list-none grid-cols-2 gap-2 p-4 text-grey-900 max-lg:items-stretch lg:flex lg:size-full lg:items-center lg:gap-0 lg:p-0 lg:px-12 xl:px-0 desktop:mx-auto desktop:max-w-content">
        {links.map((link, index) => {
          const { url, image, icon, label } = link?.fields || {}
          const isLastItem = index === links.length - 1
          const spanFullWidthOnMobile = isLastItem && lastItemSpansFullWidth
          const linkContent = (
            <>
              {!!icon && <DynamicIcon name={icon} weight="bold" className="size-8 text-primary-500 group-hover:text-accent-3 lg:size-6" />}
              {!icon && !!image?.fields?.file && (
                <Image src={getImageUrl(image)} height={25} width={25} alt={image?.fields?.title ?? 'Quick Links Image'} className="size-6 object-cover" />
              )}
              {label ? <span className="block text-center text-sm font-semibold xl:text-base">{label}</span> : null}
            </>
          )

          return (
            <li
              key={link?.sys?.id ?? index}
              className={`w-full min-w-0 text-sm font-semibold max-lg:flex max-lg:min-h-0 lg:flex lg:min-w-0 lg:flex-1 lg:basis-0 lg:items-center lg:justify-center ${quickLinkItemClassName} ${spanFullWidthOnMobile ? 'col-span-2' : ''}`}
            >
              <Link
                href={url || ''}
                className="group flex min-h-0 w-full flex-1 flex-row items-center justify-center gap-2 rounded-lg border border-grey-200 bg-white p-4 hover:border-accent-3 hover:text-accent-3 lg:min-h-[52px] lg:flex-initial lg:border-2 lg:border-transparent lg:bg-transparent lg:px-6 lg:py-4"
              >
                {linkContent}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
